import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { fetchReportAcquisition } from '@/lib/api/reports.js';
import { exportToCsv } from '@/lib/csv-export.js';
import { formatCurrency, prettyLabel } from '@/lib/format.js';
import { useReportQueryParams } from '@/pages/reports/report-filters-context.js';

const sourceColors = ['#1B4F8A', '#E8793A', '#22c55e', '#6366f1', '#f59e0b', '#94a3b8', '#0ea5e9', '#ec4899'];

export function AcquisitionReportPage() {
  const qBuilt = useReportQueryParams();

  const q = useQuery({
    queryKey: ['reports-acquisition', qBuilt],
    queryFn: () => fetchReportAcquisition(qBuilt),
  });

  const d = q.data;

  const pieData = useMemo(
    () => (d?.bySource ?? []).map((r) => ({ name: prettyLabel(r.source), value: r.count })),
    [d],
  );

  const csv = () => {
    if (!d) return;
    exportToCsv(
      d.bySource.map((r) => ({ source: r.source, count: r.count, pct: r.pct, revenue: r.revenue })),
      'acquisition.csv',
    );
  };

  if (q.isLoading) return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading acquisition report…</div>;
  if (q.isError || !d?.summary || !d.bySource) {
    return <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">Could not load acquisition report.</div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <button type="button" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50" onClick={csv}>
          Export CSV
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-500">New customers</p>
          <p className="mt-1 text-xl font-bold text-slate-950">{d.summary.totalNewCustomers}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-500">Top source</p>
          <p className="mt-1 text-xl font-bold capitalize text-slate-950">
            {d.summary.topSource ? prettyLabel(d.summary.topSource) : '—'}
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-950">How customers found us</h3>
          <div className="mt-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={90} paddingAngle={2}>
                  {pieData.map((entry, i) => (
                    <Cell key={entry.name} fill={sourceColors[i % sourceColors.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase text-slate-600">
              <tr>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Customers</th>
                <th className="px-4 py-3">% of total</th>
                <th className="px-4 py-3">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {d.bySource.map((r) => (
                <tr key={r.source} className="border-b border-slate-100">
                  <td className="px-4 py-2 font-medium capitalize">{prettyLabel(r.source)}</td>
                  <td className="px-4 py-2">{r.count}</td>
                  <td className="px-4 py-2">{r.pct.toFixed(1)}%</td>
                  <td className="px-4 py-2">{formatCurrency(r.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!d.bySource.length ? <p className="p-6 text-center text-slate-500">No new customers in this period.</p> : null}
        </div>
      </div>
    </div>
  );
}
